import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiPrinter } from "react-icons/fi";
import { APP_PATH } from "../../api/backendPath";
import { toast } from "../toast";
import { formatDateNumeric as tzDateNumeric } from "../../utils/datetime";
import { formatPercent, formatWholeAmount } from "../../utils/amountFormat";

/**
 * Shared physiotherapy invoice (print) modal. Self-contained: given an `orderId`
 * it fetches the order details + center profile, computes the totals (NO
 * insurance — physiotherapy never has it) and renders the printable invoice.
 *
 * Reused by BOTH the Order Management page and the Dashboard's Scheduled &
 * In-Progress table so the invoice is identical everywhere.
 *
 * Props:
 *  - open:    boolean — whether the modal is visible
 *  - orderId: number|string — the physiotherapy booking id to invoice
 *  - onClose: () => void
 */
// Every money amount on the invoice is rendered through this helper so the "IQD" prefix is
// applied consistently and can never be forgotten on a new row. IQD has no minor unit, so
// amounts are rounded to a whole number (e.g. a 6% admin fee of 1,200.3 shows as 1,200).
// Mirrors the lab / imaging invoices.
const iqd = (v) => `IQD ${formatWholeAmount(v)}`;

const PhysioInvoiceModal = ({ open, orderId, onClose }) => {
  const { t, i18n } = useTranslation();
  const [invoiceOrder, setInvoiceOrder] = useState(null);
  const [centerProfile, setCenterProfile] = useState(null);
  const invoiceRef = useRef(null);

  // Fetch the order details (and the center profile once) whenever the modal is
  // opened for an order. Cleared on close so a stale invoice never flashes.
  useEffect(() => {
    if (!open || !orderId) {
      setInvoiceOrder(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const token = sessionStorage.getItem("usr_1a2b3c");
        const baseURL = process.env.REACT_APP_API_URL || "";
        const res = await fetch(`${baseURL}${APP_PATH.physioOrders}/${orderId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json();
        if (cancelled) return;
        if (json?.data) {
          setInvoiceOrder(json.data);
          if (!centerProfile) {
            fetch(`${baseURL}/api/v1/physiotherapist/profile`, {
              headers: { Authorization: `Bearer ${token}` },
            })
              .then((r) => r.json())
              .then((pj) => {
                if (!cancelled && pj?.data) setCenterProfile(pj.data);
              })
              .catch(() => {});
          }
        } else {
          toast.error(json?.message || "Failed to load invoice");
          onClose?.();
        }
      } catch (e) {
        if (!cancelled) {
          toast.error("Failed to load invoice");
          onClose?.();
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, orderId]);

  const isInvoiceAr = i18n.language === "ar";
  const invL = isInvoiceAr
    ? {
        preview: "معاينة الفاتورة", print: "طباعة", close: "إغلاق", invoice: "فاتورة",
        tagline: "منصة الصحة الإلكترونية", patientDetails: "تفاصيل المريض", name: "الاسم",
        mobile: "الهاتف", ageGender: "العمر / الجنس", yr: "سنة", serviceDetails: "تفاصيل الخدمة",
        typeLocation: "النوع / الموقع", serviceDate: "تاريخ الخدمة", serviceTime: "وقت الخدمة",
        no: "#", therapy: "العلاج", sessions: "الجلسات", location: "الموقع", price: "السعر",
        subtotal: "المجموع الفرعي", adminFee: "رسوم إدارية", transportationFee: "رسوم النقل", free: "مجاناً",
        loyaltyDiscount: "خصم نقاط الولاء", pts: "نقطة", promoDiscount: "خصم الرمز الترويجي",
        total: "الإجمالي", payment: "الدفع", method: "الطريقة", status: "الحالة",
        thankyou: "شكراً لاختياركم تِرياق.", noServices: "لا توجد خدمات", notScheduled: "غير مجدول",
        homeVisit: "زيارة منزلية", onSite: "في المركز", physiotherapy: "علاج طبيعي",
      }
    : {
        preview: "Invoice Preview", print: "Print", close: "Close", invoice: "INVOICE",
        tagline: "Electronic Health Platform", patientDetails: "Patient Details", name: "Name",
        mobile: "Mobile", ageGender: "Age / Gender", yr: "yr", serviceDetails: "Service Details",
        typeLocation: "Type / Location", serviceDate: "Service Date", serviceTime: "Service Time",
        // No "(IQD)" on the price header: every amount in the column carries the prefix now.
        no: "#", therapy: "Therapy", sessions: "Sessions", location: "Location", price: "Price",
        subtotal: "Subtotal", adminFee: "Admin Fee", transportationFee: "Transportation Fee", free: "Free",
        loyaltyDiscount: "Loyalty Point Discount", pts: "pts", promoDiscount: "Promo Discount",
        total: "Total", payment: "Payment", method: "Method", status: "Status",
        thankyou: "Thank you for choosing TIRYAQ.", noServices: "No services", notScheduled: "Not scheduled",
        homeVisit: "Home Visit", onSite: "On-Site", physiotherapy: "Physiotherapy",
      };

  const fmtInvStatus = (s) => (s ? String(s).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "");

  const centerName = centerProfile?.center_name_english || centerProfile?.center_name_arabic || "TIRYAQ";
  const centerAvatar = centerProfile?.avatar_presigned_url || centerProfile?.avatar_url || null;
  const centerInitial = (centerName || "T").trim().charAt(0).toUpperCase() || "T";

  const invoiceData = (() => {
    const services = invoiceOrder?.services || [];
    const servicesTotal = services.reduce(
      (sum, s) => sum + Number(s.price || 0),
      0,
    );
    const hasHomeVisit = services.some((s) => s.location === "HOME_VISIT");
    const transportationFee = hasHomeVisit ? (Number(invoiceOrder?.transportation_fee) > 0 ? Number(invoiceOrder.transportation_fee) : 0) : 0;
    const feePercentage = Number(invoiceOrder?.admin_fee_percentage) || 0;
    const orderAdminFeeAmount = Number(invoiceOrder?.admin_fee_amount) || 0;
    const adminFee = orderAdminFeeAmount > 0 ? orderAdminFeeAmount : Math.round(((servicesTotal + transportationFee) * feePercentage) / 100);

    // Loyalty / promotion discount the patient redeemed, persisted on the booking
    // (`physiotherapy_bookings.loyalty_discount` / `.loyalty_points_used`).
    const loyaltyDiscount = Number(invoiceOrder?.loyalty_discount || 0);
    const loyaltyPointsUsed = Number(invoiceOrder?.loyalty_points_used || 0);
    // Promo code discount (gateway order-details enrichment — the booking has no promo
    // column). Printed as its own line so the derived Total below still adds up.
    const promoDiscount = Number(invoiceOrder?.promo_discount || 0);
    const promoCode = invoiceOrder?.promo_code || null;

    // Subtotal shown on the invoice = services + transportation fee. That is EXACTLY the base the
    // admin-fee percentage is charged on (adminFee = (services + delivery) × %), so a reader
    // can verify the admin-fee line themselves. Mirrors the doctor / lab / imaging invoices.
    const subtotal = servicesTotal + transportationFee;

    // Total is DERIVED from the lines printed above it, exactly like the doctor invoice:
    // Subtotal + Admin Fee − Loyalty Discount − Promo Discount. It used to print the FIRST
    // successful payment's `amount_paid`, which is net of the promo (no line for it, so the
    // invoice did not add up) and, on a revisit, belongs to an earlier session.
    // Physiotherapy has no insurance, so there is no coverage line.
    const grossTotal = subtotal + adminFee;
    const total = Math.max(0, grossTotal - loyaltyDiscount - promoDiscount);

    return {
      servicesTotal,
      subtotal,
      transportationFee,
      feePercentage,
      adminFee,
      loyaltyDiscount,
      loyaltyPointsUsed,
      promoDiscount,
      promoCode,
      total,
      hasHomeVisit,
    };
  })();

  // The invoice is addressed to the PATIENT (the account holder) — on a child booking that is
  // the guardian — exactly like the doctor app's consultation invoice, which prints the patient
  // record's name and falls back to the child's name only when the account holder's name is
  // unavailable. Kept in step with it so every provider bills the same person.
  const invoicePatientFullName =
    (invoiceOrder?.patient
      ? [invoiceOrder.patient.full_name, invoiceOrder.patient.surname].filter(Boolean).join(" ")
      : "") ||
    [invoiceOrder?.child_name, invoiceOrder?.child_surname].filter(Boolean).join(" ");
  const invoiceHasHomeVisit = (invoiceOrder?.services || []).some((s) => s.location === "HOME_VISIT");

  const handlePrintInvoiceDoc = () => {
    const printContent = invoiceRef.current;
    if (!printContent) return;
    const isRtl = i18n.language === "ar";
    const printWindow = window.open("", "_blank", "width=900,height=700");
    if (!printWindow) return;
    printWindow.document.write(`<!DOCTYPE html><html dir="${isRtl ? "rtl" : "ltr"}" lang="${i18n.language}"><head><title>Invoice - ${invoiceOrder?.booking_number || invoiceOrder?.id || ""}</title>
      <style>
        * { box-sizing: border-box; font-family: Arial, Helvetica, sans-serif; }
        body { margin: 24px; color: #333; }
        h2 { margin: 0; }
        table { width: 100%; border-collapse: collapse; }
        @media print { body { margin: 0; padding: 16px; } }
      </style></head><body>${printContent.innerHTML}</body></html>`);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 250);
  };

  if (!open || !invoiceOrder) return null;

  return (
    <div
      onClick={() => onClose?.()}
      dir={isInvoiceAr ? "rtl" : "ltr"}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999,
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        padding: "30px 16px", overflowY: "auto",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: "12px", width: "100%", maxWidth: "800px",
          maxHeight: "90vh", overflow: "auto", boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
        }}
      >
        {/* Toolbar */}
        <div
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "16px 24px", borderBottom: "1px solid #e5e7eb", position: "sticky",
            top: 0, background: "#fff", borderTopLeftRadius: "12px", borderTopRightRadius: "12px",
          }}
        >
          <h4 style={{ margin: 0, fontSize: "16px", fontWeight: 700 }}>{invL.preview}</h4>
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              type="button"
              onClick={handlePrintInvoiceDoc}
              style={{
                display: "inline-flex", alignItems: "center", gap: "6px", background: "#0cbdbd",
                color: "#fff", border: "none", borderRadius: "8px", padding: "8px 20px",
                fontWeight: 600, cursor: "pointer", fontSize: "14px",
              }}
            >
              <FiPrinter size={16} /> {invL.print}
            </button>
            <button
              type="button"
              onClick={() => onClose?.()}
              style={{
                background: "#fff", color: "#333", border: "1px solid #ddd", borderRadius: "8px",
                padding: "8px 20px", fontWeight: 600, cursor: "pointer", fontSize: "14px",
              }}
            >
              {invL.close}
            </button>
          </div>
        </div>

        {/* Printable content */}
        <div ref={invoiceRef} style={{ padding: "22px 24px" }}>
          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "2px solid #0cbdbd", paddingBottom: "16px", marginBottom: "20px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
              {centerAvatar ? (
                <img src={centerAvatar} alt="" style={{ width: "54px", height: "54px", borderRadius: "50%", objectFit: "cover", border: "1px solid #e6fafa" }} />
              ) : (
                <div style={{ width: "54px", height: "54px", borderRadius: "50%", background: "#e6fafa", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, color: "#0cbdbd", fontSize: "20px" }}>{centerInitial}</div>
              )}
              <div>
                <h2 style={{ margin: 0, fontSize: "20px", fontWeight: 700 }}>{centerName}</h2>
                <p style={{ margin: "2px 0", fontSize: "12px", color: "#999" }}>{invL.tagline}</p>
                {centerProfile?.mobile_number && (
                  <p style={{ margin: "2px 0", fontSize: "12px", color: "#999" }}>{centerProfile.mobile_number}</p>
                )}
              </div>
            </div>
            <div style={{ textAlign: isInvoiceAr ? "left" : "right" }}>
              <div style={{ fontSize: "24px", fontWeight: 700, color: "#0cbdbd" }}>{invL.invoice}</div>
              <div style={{ fontSize: "13px", color: "#666" }}>{invoiceOrder?.booking_number ? `#${invoiceOrder.booking_number}` : (invoiceOrder?.id ? `#${invoiceOrder.id}` : "")}</div>
              <div style={{ fontSize: "12px", color: "#999" }}>{fmtInvStatus(invoiceOrder?.status)}</div>
            </div>
          </div>

          {/* Patient details */}
          <div style={{ marginBottom: "18px" }}>
            <div style={{ fontSize: "14px", fontWeight: 700, marginBottom: "8px", borderBottom: "1px solid #eee", paddingBottom: "4px" }}>{invL.patientDetails}</div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", marginBottom: "4px" }}>
              <span style={{ color: "#666" }}>{invL.name}</span>
              <span>{invoicePatientFullName || "N/A"}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", marginBottom: "4px" }}>
              <span style={{ color: "#666" }}>{invL.mobile}</span>
              <span>{invoiceOrder?.patient?.mobile_number || "N/A"}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", marginBottom: "4px" }}>
              <span style={{ color: "#666" }}>{invL.ageGender}</span>
              <span>{invoiceOrder?.patient?.age != null ? `${invoiceOrder.patient.age} ${invL.yr}` : "N/A"} · {invoiceOrder?.patient?.gender || "N/A"}</span>
            </div>
          </div>

          {/* Payment */}
          <div style={{ marginTop: "18px", marginBottom: "8px" }}>
            <div style={{ fontSize: "14px", fontWeight: 700, marginBottom: "8px", borderBottom: "1px solid #eee", paddingBottom: "4px" }}>{invL.payment}</div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", marginBottom: "4px" }}>
              <span style={{ color: "#666" }}>{invL.method}</span>
              <span style={{ fontWeight: 600, textTransform: "uppercase" }}>{invoiceOrder?.payment_method || "N/A"}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", marginBottom: "4px" }}>
              <span style={{ color: "#666" }}>{invL.status}</span>
              <span
                style={{
                  fontWeight: 600,
                  color:
                    invoiceOrder?.payment_status === "REFUNDED" ||
                    invoiceOrder?.payment_status === "PARTIALLY_REFUNDED"
                      ? "#b91c1c"
                      : invoiceOrder?.payment_status === "COMPLETED" ||
                        invoiceOrder?.payment_status === "PAID"
                      ? "#00BC7D"
                      : "#e09200",
                }}
              >
                {invoiceOrder?.payment_status === "REFUNDED"
                  ? "Refunded"
                  : invoiceOrder?.payment_status === "PARTIALLY_REFUNDED"
                  ? "Partially Refunded"
                  : invoiceOrder?.payment_status || "N/A"}
              </span>
            </div>
          </div>

          {/* Service details */}
          <div style={{ marginBottom: "18px" }}>
            <div style={{ fontSize: "14px", fontWeight: 700, marginBottom: "8px", borderBottom: "1px solid #eee", paddingBottom: "4px" }}>{invL.serviceDetails}</div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", marginBottom: "4px" }}>
              <span style={{ color: "#666" }}>{invL.typeLocation}</span>
              <span>{invL.physiotherapy} · {invoiceHasHomeVisit ? invL.homeVisit : invL.onSite}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", marginBottom: "4px" }}>
              <span style={{ color: "#666" }}>{invL.serviceDate}</span>
              <span>{invoiceOrder?.scheduled_date ? tzDateNumeric(new Date(invoiceOrder.scheduled_date)) : invL.notScheduled}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", marginBottom: "4px" }}>
              <span style={{ color: "#666" }}>{invL.serviceTime}</span>
              <span>{invoiceOrder?.scheduled_time || "N/A"}</span>
            </div>
          </div>

          {/* Sessions / services table */}
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "10px" }}>
            <thead>
              <tr>
                <th style={{ background: "#f5f5f5", color: "#333", textAlign: isInvoiceAr ? "right" : "left", padding: "8px 10px", fontSize: "12px", fontWeight: 600, borderBottom: "2px solid #ddd" }}>{invL.no}</th>
                <th style={{ background: "#f5f5f5", color: "#333", textAlign: isInvoiceAr ? "right" : "left", padding: "8px 10px", fontSize: "12px", fontWeight: 600, borderBottom: "2px solid #ddd" }}>{invL.therapy}</th>
                <th style={{ background: "#f5f5f5", color: "#333", textAlign: "center", padding: "8px 10px", fontSize: "12px", fontWeight: 600, borderBottom: "2px solid #ddd" }}>{invL.sessions}</th>
                <th style={{ background: "#f5f5f5", color: "#333", textAlign: isInvoiceAr ? "right" : "left", padding: "8px 10px", fontSize: "12px", fontWeight: 600, borderBottom: "2px solid #ddd" }}>{invL.location}</th>
                <th style={{ background: "#f5f5f5", color: "#333", textAlign: isInvoiceAr ? "left" : "right", padding: "8px 10px", fontSize: "12px", fontWeight: 600, borderBottom: "2px solid #ddd" }}>{invL.price}</th>
              </tr>
            </thead>
            <tbody>
              {(invoiceOrder?.services || []).map((svc, i) => (
                <tr key={svc.id || i}>
                  <td style={{ padding: "8px 10px", fontSize: "13px", borderBottom: "1px solid #eee" }}>{i + 1}</td>
                  <td style={{ padding: "8px 10px", fontSize: "13px", borderBottom: "1px solid #eee" }}>{svc.service_name || "N/A"}</td>
                  <td style={{ padding: "8px 10px", fontSize: "13px", borderBottom: "1px solid #eee", textAlign: "center" }}>
                    {(svc.sessions_completed || 0)}/{svc.number_of_sessions || 1}
                  </td>
                  <td style={{ padding: "8px 10px", fontSize: "13px", borderBottom: "1px solid #eee" }}>{svc.location === "HOME_VISIT" ? invL.homeVisit : invL.onSite}</td>
                  <td style={{ padding: "8px 10px", fontSize: "13px", borderBottom: "1px solid #eee", textAlign: isInvoiceAr ? "left" : "right" }}>{iqd(svc.price)}</td>
                </tr>
              ))}
              {(invoiceOrder?.services || []).length === 0 && (
                <tr><td colSpan="5" style={{ textAlign: "center", padding: "10px" }}>{invL.noServices}</td></tr>
              )}
            </tbody>
            {/* The tfoot rows read as a single top-to-bottom calculation, same order as the
                doctor / lab / imaging invoices:
                  services                    (line items above)
                  + transportation fee              (home visit only)
                  = Subtotal                  (the base the admin fee % is charged on)
                  + admin fee
                  − loyalty point discount    (only when points were redeemed)
                  = Total                     (what the patient actually paid) */}
            <tfoot>
              {invoiceData.hasHomeVisit && (
                <tr>
                  <td colSpan="4" style={{ padding: "8px 10px", fontSize: "13px", borderTop: "2px solid #333", textAlign: isInvoiceAr ? "left" : "right", color: "#666" }}>{invL.transportationFee}</td>
                  <td style={{ padding: "8px 10px", fontSize: "13px", borderTop: "2px solid #333", textAlign: isInvoiceAr ? "left" : "right" }}>{invoiceData.transportationFee > 0 ? `+ ${iqd(invoiceData.transportationFee)}` : invL.free}</td>
                </tr>
              )}
              <tr>
                <td colSpan="4" style={{ padding: "8px 10px", fontSize: "13px", fontWeight: 600, borderTop: invoiceData.hasHomeVisit ? "1px solid #eee" : "2px solid #333", textAlign: isInvoiceAr ? "left" : "right", color: "#666" }}>{invL.subtotal}</td>
                <td style={{ padding: "8px 10px", fontSize: "13px", fontWeight: 600, borderTop: invoiceData.hasHomeVisit ? "1px solid #eee" : "2px solid #333", textAlign: isInvoiceAr ? "left" : "right" }}>{iqd(invoiceData.subtotal)}</td>
              </tr>
              {invoiceData.adminFee > 0 && (
                <tr>
                  <td colSpan="4" style={{ padding: "8px 10px", fontSize: "13px", textAlign: isInvoiceAr ? "left" : "right", color: "#666" }}>{invL.adminFee}{invoiceData.feePercentage > 0 ? ` (${formatPercent(invoiceData.feePercentage)}%)` : ""}</td>
                  <td style={{ padding: "8px 10px", fontSize: "13px", textAlign: isInvoiceAr ? "left" : "right" }}>+ {iqd(invoiceData.adminFee)}</td>
                </tr>
              )}
              {/* Loyalty points redeemed — shown as a deduction (green, like the doctor, lab
                  and imaging invoices) so the Total below reconciles with the amount the
                  patient was actually charged. */}
              {invoiceData.loyaltyDiscount > 0 && (
                <tr>
                  <td colSpan="4" style={{ padding: "8px 10px", fontSize: "13px", textAlign: isInvoiceAr ? "left" : "right", color: "#666" }}>{invL.loyaltyDiscount}{invoiceData.loyaltyPointsUsed > 0 ? ` (${invoiceData.loyaltyPointsUsed} ${invL.pts})` : ""}</td>
                  <td style={{ padding: "8px 10px", fontSize: "13px", textAlign: isInvoiceAr ? "left" : "right", color: "#00BC7D" }}>− {iqd(invoiceData.loyaltyDiscount)}</td>
                </tr>
              )}
              {/* Promo code discount — its own line so the derived Total still equals what the
                  patient pays. */}
              {invoiceData.promoDiscount > 0 && (
                <tr>
                  <td colSpan="4" style={{ padding: "8px 10px", fontSize: "13px", textAlign: isInvoiceAr ? "left" : "right", color: "#666" }}>{invL.promoDiscount}{invoiceData.promoCode ? ` (${invoiceData.promoCode})` : ""}</td>
                  <td style={{ padding: "8px 10px", fontSize: "13px", textAlign: isInvoiceAr ? "left" : "right", color: "#00BC7D" }}>− {iqd(invoiceData.promoDiscount)}</td>
                </tr>
              )}
              <tr>
                <td colSpan="4" style={{ padding: "10px", fontWeight: 700, borderTop: "1px solid #ccc", textAlign: isInvoiceAr ? "left" : "right" }}>{invL.total}</td>
                <td style={{ padding: "10px", fontWeight: 700, borderTop: "1px solid #ccc", textAlign: isInvoiceAr ? "left" : "right", color: "#0cbdbd" }}>{iqd(invoiceData.total)}</td>
              </tr>
            </tfoot>
          </table>

          {/* Footer — show the center name (not the platform name). */}
          <div style={{ marginTop: "26px", textAlign: "center", fontSize: "12px", color: "#999", borderTop: "1px solid #eee", paddingTop: "12px" }}>
            {`${isInvoiceAr ? "شكراً لاختياركم" : "Thank you for choosing"} ${centerName}.`}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhysioInvoiceModal;
